import React, { useEffect, useRef, useState, useCallback, useMemo } from "react";
import ImageCropModal from "../../commonComponents/ImageCropModal";
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  IconButton,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useColorModeValue,
  useDisclosure,
  useColorMode,
  List,
  ListItem,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  SimpleGrid,
  Skeleton,
  VStack,
  HStack,
  Badge,
} from "@chakra-ui/react";
import {
  AddIcon,
  CloseIcon,
  DeleteIcon,
  EditIcon,
  SearchIcon,
  CheckIcon,
} from "@chakra-ui/icons";
import API, { getCompressedFile, getMediaURL } from "../../context/API";
import { invalidateCategoryCache, updateCategorySequence } from "../../APIs/menuAPI";
import SearchBar from "../../theme/Layout/Searchbar";
import { GrUpdate } from "react-icons/gr";
import { Reorder } from "framer-motion";
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import useCommonToast from "../../utils/toast";
import { FaPizzaSlice } from "react-icons/fa";
import { Icon } from "@chakra-ui/react";

const ItemTypes = {
  SELECTED_ITEM: 'selectedItem',
};

// Optimized DraggableItem component
const DraggableItem = React.memo(({ item, index, moveItem, handleRemoveSelectedItem }) => {
  const [, ref] = useDrag({
    type: ItemTypes.SELECTED_ITEM,
    item: { index },
  });

  const [, drop] = useDrop({
    accept: ItemTypes.SELECTED_ITEM,
    hover: (draggedItem) => {
      if (draggedItem.index !== index) {
        moveItem(draggedItem.index, index);
        draggedItem.index = index;
      }
    },
  });

  return (
    <Button
      ref={(node) => ref(drop(node))}
      key={item.id}
      size="sm"
      m="1"
      colorScheme="blue"
      onClick={() => handleRemoveSelectedItem(item.id)}
      rightIcon={<CloseIcon boxSize={2} />}
    >
      {item.item}
    </Button>
  );
});

DraggableItem.displayName = 'DraggableItem';

const DraggableItems = React.memo(({ selectedItems, moveItem, handleRemoveSelectedItem }) => {
  return (
    <DndProvider backend={HTML5Backend}>
      <Flex wrap="wrap" mt="4">
        {selectedItems.map((item, index) => (
          <DraggableItem
            key={item.id}
            item={item}
            index={index}
            moveItem={moveItem}
            handleRemoveSelectedItem={handleRemoveSelectedItem}
          />
        ))}
      </Flex>
    </DndProvider>
  );
});

DraggableItems.displayName = 'DraggableItems';

// Optimized search bar with debouncing
const CategoriesSearchBar = React.memo(({
  items,
  onSearch,
  onSuggestionClick,
  placeholder = "Search...",
  selectedItems = [],
  ...props
}) => {
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const { colorMode } = useColorMode();
  const searchBarRef = useRef(null);
  const debounceRef = useRef(null);

  const fetchSuggestions = useCallback((query) => {
    console.log('🔍 CategoriesSearchBar fetchSuggestions called with:', {
      query,
      itemsLength: items?.length,
      itemsSample: items?.slice(0, 3)
    });
    
    if (!items || !Array.isArray(items)) {
      console.log('❌ No items available for search');
      return [];
    }
    
    const results = items.filter((item) =>
      item.toLowerCase().includes(query.toLowerCase())
    ).slice(0, 10);
    
    console.log('✅ Search results:', results);
    return results;
  }, [items]);

  // Debounced search effect
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    
    debounceRef.current = setTimeout(() => {
      if (query) {
        setSuggestions(fetchSuggestions(query));
        // Only call onSearch after debounce delay
        onSearch(query);
      } else {
        setSuggestions([]);
        onSearch("");
      }
    }, 500); // Increased debounce to 500ms for better UX

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, fetchSuggestions, onSearch]);

  const handleInputChange = (e) => {
    const newQuery = e.target.value;
    setQuery(newQuery);
    // Don't call onSearch immediately - let debounce handle it
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      // Clear debounce and search immediately on Enter
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      onSearch(query);
      setSuggestions([]);
    } else if (e.key === "Escape") {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    if (!selectedItems.some((item) => item.item === suggestion)) {
      onSuggestionClick(suggestion);
    }
    setQuery(suggestion);
    setSuggestions([]);
    // Search immediately when suggestion is clicked
    onSearch(suggestion);
  };

  const clearSearch = () => {
    setQuery("");
    setSuggestions([]);
    onSearch("");
  };

  const handleClickOutside = (event) => {
    if (searchBarRef.current && !searchBarRef.current.contains(event.target)) {
      setSuggestions([]);
    }
  };

  const renderSuggestions = () => {
    return suggestions.map((suggestion, index) => {
      const isSelected = selectedItems.some((item) => item.item === suggestion);
      return (
        <ListItem
          key={index}
          p={3}
          cursor="pointer"
          borderRadius="md"
          _hover={{ bg: suggestionHoverBg }}
          color={suggestionTextColor}
          onClick={() => handleSuggestionClick(suggestion)}
        >
          <Flex alignItems="center">
            {suggestion}
            {isSelected && <CheckIcon ml="auto" color="green.500" />}
          </Flex>
        </ListItem>
      );
    });
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const suggestionBg = colorMode === "light" ? "whiteAlpha.900" : "gray.700";
  const suggestionHoverBg = colorMode === "light" ? "gray.100" : "gray.600";
  const suggestionTextColor = colorMode === "light" ? "gray.800" : "gray.200";

  return (
    <Box position="relative" ref={searchBarRef} {...props}>
      <InputGroup mb="4">
        <InputLeftElement pointerEvents="none">
          <SearchIcon color="gray.500" />
        </InputLeftElement>
        <Input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          variant="filled"
          borderRadius="full"
          {...props}
        />
        {query && (
          <InputRightElement>
            <IconButton
              icon={<CloseIcon />}
              size="sm"
              onClick={clearSearch}
              variant="ghost"
              aria-label="Clear search"
            />
          </InputRightElement>
        )}
      </InputGroup>
      {suggestions.length > 0 && (
        <Box
          position="absolute"
          top="100%"
          left="0"
          right="0"
          zIndex="10"
          bg={suggestionBg}
          borderRadius="lg"
          boxShadow="lg"
          maxHeight="200px"
          overflowY="auto"
          backdropFilter="blur(10px)"
        >
          <List spacing={1}>{renderSuggestions()}</List>
        </Box>
      )}
    </Box>
  );
});

CategoriesSearchBar.displayName = 'CategoriesSearchBar';

// Category Card Component for mobile view
const CategoryCard = React.memo(({ category, onEdit, onDelete, isSelected, onSelect }) => {
  const bgColor = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const hoverBgColor = useColorModeValue("gray.50", "gray.700");
  
  return (
    <Box
      bg={bgColor}
      border="1px solid"
      borderColor={isSelected ? "blue.500" : borderColor}
      borderRadius="lg"
      p={4}
      cursor="pointer"
      transition="all 0.2s"
      minH="200px"
      display="flex"
      flexDirection="column"
      _hover={{
        transform: "translateY(-2px)",
        boxShadow: "lg",
        bg: hoverBgColor,
      }}
      onClick={() => onSelect(category)}
    >
      {/* Header Section with consistent height */}
      <Flex align="center" justify="space-between" mb={3} minH="40px">
        <Box flex="1" minW="0">
          <Text 
            fontWeight="bold" 
            fontSize="lg" 
            mb={1} 
            noOfLines={2}
            lineHeight="1.2"
          >
            {category.category}
          </Text>
          <Badge colorScheme="blue" variant="subtle" fontSize="sm">
            {category.items?.length || 0} items
          </Badge>
        </Box>
        <HStack spacing={2} flexShrink={0}>
          <IconButton
            icon={<EditIcon />}
            size="sm"
            colorScheme="blue"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(category);
            }}
          />
          <IconButton
            icon={<DeleteIcon />}
            size="sm"
            colorScheme="red"
            variant="ghost"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(category.id);
            }}
          />
        </HStack>
      </Flex>
      
      {/* Image Section with consistent height */}
      <Box flex="1" display="flex" alignItems="center" justifyContent="center" minH="120px">
        {(() => {
          console.log(`🖼️ CategoryCard for "${category.category}":`, {
            picture: category.picture,
            pictureType: typeof category.picture,
            pictureTrimmed: typeof category.picture === 'string' ? category.picture.trim() : 'not a string',
            hasPicture: category.picture && typeof category.picture === 'string' && category.picture.trim() !== ""
          });
          
          const imageUrl = category.picture && typeof category.picture === 'string' && category.picture.trim() !== "" 
            ? getMediaURL(category.picture) 
            : null;
          
          console.log(`🔗 Generated image URL for "${category.category}":`, imageUrl);
          
          return imageUrl ? (
            <Image
              src={imageUrl}
              alt={category.category}
              width="100%"
              height="120px"
              objectFit="cover"
              borderRadius="md"
              fallbackSrc="https://via.placeholder.com/300x120"
              onError={(e) => {
                console.log(`❌ Image failed to load for "${category.category}":`, e.target.src);
                e.target.src = "https://via.placeholder.com/300x120";
              }}
              onLoad={(e) => {
                console.log(`✅ Image loaded successfully for "${category.category}":`, e.target.src);
              }}
            />
          ) : (
            <Box
              width="100%"
              height="120px"
              bg="gray.100"
              borderRadius="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Text fontSize="sm" color="gray.500">No Image</Text>
            </Box>
          );
        })()}
      </Box>
    </Box>
  );
});

CategoryCard.displayName = 'CategoryCard';

const ItemsListSection = React.memo(({
  items,
  itemSearchQuery,
  setItemSearchQuery,
  selectedItems,
  handleItemSelect,
  moveItem,
  handleRemoveSelectedItem,
}) => {
  const listBorderColor = useColorModeValue("gray.200", "gray.600");
  const selectedBgLight = useColorModeValue("blue.50", "blue.900");
  const selectedBgDark = useColorModeValue("blue.100", "blue.800");
  const selectedBorderLight = useColorModeValue("blue.200", "blue.700");
  const hoverBgLight = useColorModeValue("gray.100", "gray.700");
  const textColorLight = useColorModeValue("gray.800", "gray.200");

  return (
    <Box>
      <InputGroup mb={4} size="lg">
        <InputLeftElement 
          pointerEvents="none"
          height="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <SearchIcon color="gray.500" boxSize={5} />
        </InputLeftElement>
        <Input
          type="text"
          placeholder="Search items..."
          value={itemSearchQuery}
          onChange={(e) => setItemSearchQuery(e.target.value)}
          variant="filled"
          borderRadius="full"
          size="lg"
          pl={10}
          pr={itemSearchQuery ? 10 : 4}
        />
        {itemSearchQuery && (
          <InputRightElement
            height="100%"
            display="flex"
            alignItems="center"
            justifyContent="center"
            pr={2}
          >
            <IconButton
              icon={<CloseIcon />}
              size="sm"
              onClick={() => setItemSearchQuery("")}
              variant="ghost"
              aria-label="Clear search"
              borderRadius="full"
            />
          </InputRightElement>
        )}
      </InputGroup>
      
      {/* Items List */}
      <Box
        maxH="300px"
        overflowY="auto"
        border="1px solid"
        borderColor={listBorderColor}
        borderRadius="lg"
        p={2}
      >
        {items.length === 0 ? (
          <Text p={4} textAlign="center" color="gray.500">
            {itemSearchQuery ? "No items found" : "No items available"}
          </Text>
        ) : (
          <List spacing={1}>
            {items.map((item) => {
              const isSelected = selectedItems.some((selected) => selected.id === item.id);
              
              return (
                <ListItem
                  key={item.id}
                  p={3}
                  cursor="pointer"
                  borderRadius="md"
                  bg={isSelected ? selectedBgLight : "transparent"}
                  border="1px solid"
                  borderColor={isSelected ? selectedBorderLight : "transparent"}
                  _hover={{ 
                    bg: isSelected ? selectedBgDark : hoverBgLight
                  }}
                  onClick={() => {
                    if (isSelected) {
                      handleRemoveSelectedItem(item.id);
                    } else {
                      handleItemSelect(item.item);
                    }
                  }}
                >
                  <Flex alignItems="center" justify="space-between">
                    <Text 
                      fontWeight={isSelected ? "semibold" : "normal"}
                      color={textColorLight}
                    >
                      {item.item}
                    </Text>
                    {isSelected && (
                      <CheckIcon color="blue.500" boxSize={4} />
                    )}
                  </Flex>
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>

      {selectedItems.length > 0 && (
        <Box mt={4}>
          <FormLabel mb={2}>Selected Items</FormLabel>
          <DraggableItems
            selectedItems={selectedItems}
            moveItem={moveItem}
            handleRemoveSelectedItem={handleRemoveSelectedItem}
          />
        </Box>
      )}
    </Box>
  );
});

ItemsListSection.displayName = 'ItemsListSection';

const Categories = ({ tabIndex, onCategoryChanged }) => {
  const [categories, setCategories] = useState([]);
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentCategory, setCurrentCategory] = useState({
    id: null,
    category: "",
    picture: "",
    sequence_number: 0,
    items: [],
  });
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState(""); // For category search
  const [itemSearchQuery, setItemSearchQuery] = useState(""); // For item search in modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const cancelRef = useRef();
  
  const { showImageUploadError, showApiError, showSuccess } = useCommonToast();
  const [imageFile, setImageFile] = useState(null);
  const [cropSrc, setCropSrc] = useState(null);
  const imageInputRef = useRef(null);
  const reorderTimeoutRef = useRef(null);
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  // Custom close function to reset state
  const handleClose = useCallback(() => {
    setCurrentCategory({
      id: null,
      category: "",
      picture: "",
      sequence_number: 0,
      items: [],
    });
    setSelectedItems([]);
    setImageFile(null);
    setItemSearchQuery(""); // Clear item search query when closing modal
    onClose();
  }, [onClose]);

  // Memoized filtered categories
  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return categories;
    return categories.filter(category =>
      category.category.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [categories, searchQuery]);

  // Add a filtered category order that respects the search
  const filteredCategoryOrder = useMemo(() => {
    if (!searchQuery.trim()) return categoryOrder;
    return categoryOrder.filter(id => {
      const category = categories.find(cat => cat.id === id);
      return category && category.category.toLowerCase().includes(searchQuery.toLowerCase());
    });
  }, [categoryOrder, categories, searchQuery]);

  // Memoize categories for faster lookup in edit handler
  const categoryMap = useMemo(() => {
    const map = new Map();
    categories.forEach(category => {
      map.set(category.id, category);
    });
    return map;
  }, [categories]);

  // Filter items based on search query for modal
  const filteredItemsList = useMemo(() => {
    if (!itemSearchQuery.trim()) {
      return items || [];
    }
    const query = itemSearchQuery.toLowerCase();
    return (items || []).filter((item) =>
      item && item.item && item.item.toLowerCase().includes(query)
    );
  }, [items, itemSearchQuery]);

  const fetchItems = useCallback(async () => {
    try {
      const response = await API.get("/items");
      
      if (!response.data?.data || !Array.isArray(response.data.data)) {
        setItems([]);
        return;
      }

      const validItems = response.data.data.filter(item =>
        item && typeof item === 'object' && item.id != null
      );
      
      setItems(validItems);
    } catch (error) {
      console.error('❌ Error fetching items:', error);
      setItems([]);
    }
  }, []);

  const fetchCategories = useCallback(async () => {
    setError(null);
    
    try {
      const response = await API.get("/categories");
      
      if (!response.data?.data || !Array.isArray(response.data.data)) {
        throw new Error("Invalid response structure from API");
      }
      
      const categoriesData = response.data.data
        .filter(category => category && typeof category === 'object')
        .map((category) => {
          const itemsArray = category.items || [];
          const itemsData = Array.isArray(itemsArray)
            ? itemsArray
                .filter(item => item && typeof item === 'object')
                .map((item) => ({
                  ...item,
                  picture: item.picture ? getMediaURL(item.picture) : '',
                }))
            : [];

          itemsData.sort((a, b) => {
            const aSeq = typeof a.sequence_number === 'number' ? a.sequence_number : 0;
            const bSeq = typeof b.sequence_number === 'number' ? b.sequence_number : 0;
            return aSeq - bSeq;
          });

          return {
            ...category,
            picture: (category.picture && typeof category.picture === 'string') ? category.picture : '',
            items: itemsData
              .filter(item => item && item.id != null)
              .map((item) => item.id.toString()),
          };
        });

      categoriesData.sort((a, b) => {
        const aSeq = typeof a.sequence_number === 'number' ? a.sequence_number : 0;
        const bSeq = typeof b.sequence_number === 'number' ? b.sequence_number : 0;
        return aSeq - bSeq;
      });

      setCategories(categoriesData);
      setCategoryOrder(categoriesData
        .filter(cat => cat && cat.id != null)
        .map((cat) => cat.id));

    } catch (error) {
      setError(error.message || "Failed to fetch categories");
      showApiError("Error loading categories", error.response?.data?.message || error.message || "Unable to load categories. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [showApiError]);

  useEffect(() => {
    if (tabIndex === 2) {
      setSearchQuery("");
      fetchCategories();
      fetchItems();
    }

    return () => {};
    // Only run when tab changes; omit fetchCategories/fetchItems to avoid infinite loop (toast refs change every render)
  }, [tabIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    return () => {
      if (reorderTimeoutRef.current) {
        clearTimeout(reorderTimeoutRef.current);
      }
    };
  }, []);

  const handleItemSelect = useCallback((selectedItemName) => {
    if (!items || !Array.isArray(items)) return;
    
    const selectedItem = items.find((item) => item.item === selectedItemName);
    if (selectedItem && !selectedItems.some((item) => item.id === selectedItem.id)) {
      const newSelectedItems = [...selectedItems, selectedItem];
      setSelectedItems(newSelectedItems);
      setCurrentCategory(prev => ({
        ...prev,
        items: newSelectedItems,
      }));
    }
  }, [items, selectedItems]);

  // Handler for category search (main search bar)
  const handleSearch = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const handleRemoveSelectedItem = useCallback((itemId) => {
    const newSelectedItems = selectedItems.filter((item) => item.id !== itemId);
    setSelectedItems(newSelectedItems);
    setCurrentCategory(prev => ({
      ...prev,
      items: newSelectedItems,
    }));
  }, [selectedItems]);

  const handleCreate = useCallback(() => {
    console.log('🔍 Creating new category, checking items availability:', {
      itemsLength: items?.length,
      itemsSample: items?.slice(0, 3)
    });
    
    setCurrentCategory({
      id: null,
      category: "",
      picture: "",
      sequence_number: 0,
      items: [],
    });
    setSelectedItems([]);
    setImageFile(null);
    setItemSearchQuery(""); // Clear item search query when creating new category
    onOpen();
  }, [onOpen, items]);

  // Optimized edit handler using categoryMap for faster lookup
  const handleEdit = useCallback((category) => {
    // Use memoized category map for faster lookup
    const fullCategory = categoryMap.get(category.id) || category;
    
    // Ensure picture is always a string, not an object
    const safeCategory = {
      ...fullCategory,
      picture: typeof fullCategory.picture === 'string' ? fullCategory.picture : ""
    };
    
    setCurrentCategory(safeCategory);
    
    // Optimize items filtering
    const categoryItems = items.filter((item) => 
      item && item.id != null && fullCategory.items.includes(item.id.toString())
    );
    
    setSelectedItems(categoryItems);
    setImageFile(null);
    onOpen();
  }, [items, onOpen, categoryMap]);

  const handleDelete = useCallback((categoryId) => {
    const category = categoryMap.get(categoryId);
    if (category) {
      setCurrentCategory(category);
      setIsDeleteDialogOpen(true);
    }
  }, [categoryMap]);

  const confirmDelete = useCallback(async () => {
    try {
      await API.delete(`/categories/${currentCategory.id}`);
      showSuccess("Category deleted successfully.");
      
      // Immediate local state update
      setCategories(prevCategories => 
        prevCategories.filter(cat => cat.id !== currentCategory.id)
      );
      setCategoryOrder(prevOrder => 
        prevOrder.filter(id => id !== currentCategory.id)
      );
      
      // Invalidate cache and refresh from API
      invalidateCategoryCache();
      fetchCategories();
      
      // Trigger parent component refresh
      if (onCategoryChanged) onCategoryChanged();
      
      setIsDeleteDialogOpen(false);
    } catch (error) {
      showApiError("Error deleting category", error.response?.data?.message || error.message);
    }
  }, [currentCategory.id, showSuccess, showApiError, fetchCategories, onCategoryChanged]);

  const handleSubmit = useCallback(async () => {
    if (!currentCategory.category.trim()) {
      showApiError("Validation Error", "Category name is required");
      return;
    }

    setSubmitting(true);
    try {
      // Ensure items array contains proper integers
      const itemsArray = selectedItems
        .map((item) => item.id)
        .filter((id) => id != null) // Remove null/undefined IDs
        .map((id) => parseInt(id, 10)); // Convert to integers
      
      console.log('🔍 Items array processing:', {
        selectedItems: selectedItems,
        rawIds: selectedItems.map(item => item.id),
        processedItemsArray: itemsArray,
        allAreIntegers: itemsArray.every(id => Number.isInteger(id))
      });
      
      if (imageFile) {
        // Upload image separately first (like items do)
        console.log('📤 Uploading image separately...');
        const compressedFile = await getCompressedFile(imageFile);
        const imageFormData = new FormData();
        imageFormData.append("file", compressedFile);
        imageFormData.append("type", "kiwikiosk_categories");
        
        let uploadedImagePath = "";
        try {
          const imageResponse = await API.post("/images/upload", imageFormData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });
          uploadedImagePath = imageResponse.data.url;
          console.log('✅ Image uploaded successfully:', uploadedImagePath);
        } catch (error) {
          const errorMessage = error.response?.data?.message || error.response?.data || "Unable to upload the image.";
          showImageUploadError(errorMessage);
          setSubmitting(false);
          return;
        }
        
        // Now send category data with the image path
        const requestData = {
          category: currentCategory.category,
          sequence_number: currentCategory.sequence_number,
          items: itemsArray,
          picture: uploadedImagePath
        };
        
        console.log('🔍 Category request with uploaded image:', {
          category: currentCategory.category,
          sequence_number: currentCategory.sequence_number,
          itemsArray: itemsArray,
          picture: uploadedImagePath
        });

        if (currentCategory.id) {
          // Update existing category
          const response = await API.put(`/categories/${currentCategory.id}`, requestData);
          console.log("🔍 Category update response:", response.data);
          if (response.data.success) {
            // Update local state immediately with the new picture
            const updatedPicture = typeof response.data.data.picture === 'string' ? response.data.data.picture : "";
            console.log("🖼️ Updated picture from API:", updatedPicture);
            
            setCategories(prevCategories => 
              prevCategories.map(cat => 
                cat.id === currentCategory.id 
                  ? { 
                      ...cat, 
                      category: currentCategory.category,
                      items: selectedItems,
                      sequence_number: currentCategory.sequence_number,
                      picture: updatedPicture // Update with new picture from response
                    }
                  : cat
              )
            );
            showSuccess("Category updated successfully.");
          }
        } else {
          // Create new category
          const response = await API.post("/categories", requestData);
          if (response.data.success) {
            // Add new category to local state immediately
            // Extract picture before spreading to avoid object contamination
            const { picture: _, ...categoryDataWithoutPicture } = response.data.data;
            const responsePicture = typeof response.data.data.picture === 'string' ? response.data.data.picture : "";
            const newCategory = {
              ...categoryDataWithoutPicture,
              items: selectedItems,
              picture: responsePicture
            };
            console.log('✅ New category created with image:', { 
              originalPicture: response.data.data.picture, 
              normalizedPicture: responsePicture,
              newCategory 
            });
            setCategories(prevCategories => [newCategory, ...prevCategories]);
            showSuccess("Category created successfully.");
          }
        }
      } else {
        // Use JSON when there's no image file
        // Ensure picture is always a string, never an object
        let picturePath = "";
        if (currentCategory.picture && typeof currentCategory.picture === 'string') {
          picturePath = currentCategory.picture;
        }
        
        const requestData = {
          category: currentCategory.category,
          sequence_number: currentCategory.sequence_number,
          items: itemsArray,
          picture: picturePath
        };
        
        console.log('🔍 JSON requestData creation:', {
          category: currentCategory.category,
          sequence_number: currentCategory.sequence_number,
          itemsArray: itemsArray,
          itemsLength: itemsArray.length,
          selectedItems: selectedItems,
          requestData: requestData
        });

        if (currentCategory.id) {
          // Update existing category
          const response = await API.put(`/categories/${currentCategory.id}`, requestData);
          if (response.data.success) {
            // Update local state immediately with the picture from response
            const responsePicture = typeof response.data.data.picture === 'string' ? response.data.data.picture : "";
            setCategories(prevCategories => 
              prevCategories.map(cat => 
                cat.id === currentCategory.id 
                  ? { 
                      ...cat, 
                      category: currentCategory.category,
                      items: selectedItems,
                      sequence_number: currentCategory.sequence_number,
                      picture: responsePicture // Update with picture from response
                    }
                  : cat
              )
            );
            showSuccess("Category updated successfully.");
          }
        } else {
          // Create new category
          const response = await API.post("/categories", requestData);
          if (response.data.success) {
            // Add new category to local state immediately
            // Extract picture before spreading to avoid object contamination
            const { picture: _, ...categoryDataWithoutPicture } = response.data.data;
            const responsePicture = typeof response.data.data.picture === 'string' ? response.data.data.picture : "";
            const newCategory = {
              ...categoryDataWithoutPicture,
              items: selectedItems,
              picture: responsePicture
            };
            console.log('✅ New category created (JSON):', { 
              originalPicture: response.data.data.picture, 
              normalizedPicture: responsePicture,
              newCategory 
            });
            setCategories(prevCategories => [newCategory, ...prevCategories]);
            showSuccess("Category created successfully.");
          }
        }
      }

      handleClose();
      // Clear search query to show all items including the new one
      setSearchQuery("");
      
      // Invalidate cache to ensure fresh data
      invalidateCategoryCache();
      
      // Refetch categories to ensure the new category appears immediately
      await fetchCategories();
      
      // Trigger parent component refresh immediately
      if (onCategoryChanged) onCategoryChanged();
    } catch (error) {
      showApiError("Error saving category", error.response?.data?.message || error.message);
    } finally {
      setSubmitting(false);
    }
  }, [currentCategory, selectedItems, imageFile, handleClose, onCategoryChanged, showSuccess, showApiError, showImageUploadError, fetchCategories]);

  const handleReorder = useCallback(async (newOrder) => {
    // Immediate UI update
    setCategoryOrder(newOrder);
    
    const reorderedCategories = newOrder.map((id, index) => {
      const category = categories.find((cat) => cat.id === id);
      return {
        ...category,
        sequence_number: index + 1,
      };
    });

    setCategories(reorderedCategories);

    // Debounce API call - only send after user stops dragging
    if (reorderTimeoutRef.current) {
      clearTimeout(reorderTimeoutRef.current);
    }

    reorderTimeoutRef.current = setTimeout(async () => {
      try {
        const reorderedData = newOrder.map((id, index) => ({
          id: parseInt(id),
          sequence_number: index + 1,
        }));
        await updateCategorySequence(reorderedData);
        showSuccess("Categories reordered successfully.");
      } catch (error) {
        showApiError("Error updating order", error.response?.data?.message || error.message);
      }
    }, 500); // Wait 500ms after last drag
  }, [categories, showSuccess, showApiError]);

  const handleImageChange = useCallback((e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (imageInputRef.current) imageInputRef.current.value = '';
    setCropSrc(URL.createObjectURL(file));
  }, []);

  const handleCropDone = useCallback((file) => {
    setCropSrc(null);
    setImageFile(file);
  }, []);

  const handleCropCancel = useCallback(() => setCropSrc(null), []);

  const moveItem = useCallback((fromIndex, toIndex) => {
    const updatedItems = [...selectedItems];
    const [movedItem] = updatedItems.splice(fromIndex, 1);
    updatedItems.splice(toIndex, 0, movedItem);

    const reorderedItems = updatedItems.map((item, index) => ({
      ...item,
      sequence_number: index,
    }));

    setSelectedItems(reorderedItems);
  }, [selectedItems]);

  const bgColor = useColorModeValue("white", "gray.800");

  // Color mode values for the new UI
  const cardBgColor = useColorModeValue("white", "gray.800");
  const cardBorderColor = useColorModeValue("gray.200", "gray.700");
  const cardHoverBorderColor = useColorModeValue("blue.200", "blue.600");
  const containerBorderColor = useColorModeValue("gray.100", "gray.700");
  const headerTextColor = useColorModeValue("gray.800", "white");
  const headerSubtextColor = useColorModeValue("gray.600", "gray.400");
  const fallbackBgColor = useColorModeValue("gray.50", "gray.700");
  const idTextColor = useColorModeValue("gray.500", "gray.400");

  if (loading) {
    return (
      <Box p={4}>
        <VStack spacing={4} align="stretch">
          <Skeleton height="40px" />
          <Skeleton height="200px" />
          <Skeleton height="200px" />
        </VStack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={4} textAlign="center">
        <Text color="red.500" fontSize="lg">{error}</Text>
        <Button mt={4} onClick={fetchCategories} colorScheme="blue">
          Retry
        </Button>
      </Box>
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <Box my={4}>
        <Flex justify="space-between" align="center" mb={4}>
          <Box>
            <Heading size={{ base: "lg", md: "xl" }} letterSpacing={-2}>
              Categories
            </Heading>
            <Text fontSize="sm" color="gray.600">
              List of all available categories, you can add new ones, edit and/or delete older ones.
            </Text>
          </Box>
          <Button
            display={{ base: "none", md: "flex" }}
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={handleCreate}
          >
            Create Category
          </Button>
        </Flex>
        
        <Box p={8} bg={bgColor} borderRadius="lg" textAlign="center">
          <Text fontSize="lg" color="gray.500" mb={4}>
            No categories found. Create your first category to get started.
          </Text>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={handleCreate}
            size="lg"
          >
            Create Your First Category
          </Button>
        </Box>
      </Box>
    );
  }

  return (
    <>
    <Box my={4}>
      {/* Header Section */}
      <Flex 
        direction={{ base: "column", md: "row" }}
        justify="space-between" 
        align={{ base: "stretch", md: "center" }} 
        mb={6}
        gap={4}
      >
        <Box>
          <Heading size={{ base: "lg", md: "xl" }} letterSpacing={-2} mb={2}>
            Categories
          </Heading>
          <Text fontSize="sm" color="gray.600">
            Manage your menu categories. Create, edit, and organize them as needed.
          </Text>
        </Box>
        
        <HStack spacing={3}>
          <Button
            display={{ base: "none", md: "flex" }}
            leftIcon={<AddIcon />}
            colorScheme="blue"
            onClick={handleCreate}
            size="md"
          >
            Create Category
          </Button>
          <IconButton
            display={{ base: "flex", md: "none" }}
            icon={<AddIcon />}
            aria-label="Create Category"
            colorScheme="blue"
            onClick={handleCreate}
            size="lg"
          />
        </HStack>
      </Flex>

      {/* Search Bar */}
      <Box mb={6}>
        <SearchBar
          items={categories.map((category) => category.category)}
          onSearch={handleSearch}
          placeholder="Search categories"
          initialValue={searchQuery}
          variant="filled"
          borderRadius="lg"
          size="lg"
        />
      </Box>

      {/* Categories Display */}
      <>
        {/* Mobile/Tablet View - Card Layout */}
        <Box display={{ base: "block", lg: "none" }} mb={6}>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {filteredCategories.map((category) => (
              <CategoryCard
                key={category.id}
                category={category}
                onEdit={handleEdit}
                onDelete={handleDelete}
                isSelected={category.id === currentCategory?.id}
                onSelect={() => handleEdit(category)}
              />
            ))}
          </SimpleGrid>
        </Box>

        {/* Desktop View - Modern Card Layout */}
        <Box 
          display={{ base: "none", lg: "block" }}
          p={6} 
          bg={bgColor} 
          borderRadius="2xl" 
          boxShadow="xl"
          border="1px solid"
          borderColor={containerBorderColor}
        >
          {/* Header with stats */}
          <Flex justify="space-between" align="center" mb={8}>
            <Box>
              <Text fontSize="2xl" fontWeight="bold" color={headerTextColor}>
                {filteredCategories.length} Categories
              </Text>
              <Text fontSize="sm" color={headerSubtextColor}>
                Manage your menu categories
              </Text>
            </Box>
            <HStack spacing={4}>
              <Box textAlign="center">
                <Text fontSize="3xl" fontWeight="bold" color="blue.500">
                  {filteredCategories.reduce((total, cat) => total + (cat.items?.length || 0), 0)}
                </Text>
                <Text fontSize="sm" color={headerSubtextColor}>
                  Total Items
                </Text>
              </Box>
            </HStack>
          </Flex>

          {/* Categories Grid */}
          <Reorder.Group
            axis="y"
            values={filteredCategoryOrder} // Use filtered order instead of categoryOrder
            onReorder={handleReorder}
            style={{ width: '100%' }}
          >
            <VStack spacing={4} align="stretch" width="100%">
              {filteredCategoryOrder.map((id) => { // Use filtered order here too
                const category = categories.find((cat) => cat.id === id);
                if (!category) return null;
                
                return (
                  <Reorder.Item
                    key={id}
                    value={id}
                    style={{ width: '100%', listStyle: 'none' }}
                  >
                    <Box
                      bg={cardBgColor}
                      borderRadius="xl"
                      p={4}
                      cursor="grab"
                      border="1px solid"
                      borderColor={cardBorderColor}
                      boxShadow="md"
                      _hover={{
                        boxShadow: "lg",
                        borderColor: cardHoverBorderColor,
                      }}
                      _active={{ cursor: "grabbing" }}
                      position="relative"
                      width="100%"
                    >
                      <Flex gap={4} align="stretch">
                        {/* Left Side - Image */}
                        <Box
                          position="relative"
                          borderRadius="lg"
                          overflow="hidden"
                          width="120px"
                          height="120px"
                          bg={fallbackBgColor}
                          flexShrink={0}
                        >
                          {category.picture && category.picture !== '' ? (
                            <Image
                              src={category.picture}
                              alt={category.category}
                              width="100%"
                              height="100%"
                              objectFit="cover"
                              fallbackSrc="https://via.placeholder.com/120x120"
                            />
                          ) : (
                            <Flex
                              direction="column"
                              align="center"
                              justify="center"
                              height="100%"
                              bg="linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                              color="white"
                            >
                              <Icon 
                                as={FaPizzaSlice} 
                                boxSize={6} 
                                mb={1}
                                opacity={0.8}
                              />
                              <Text fontSize="xs" fontWeight="medium" textAlign="center" lineHeight="1.2">
                                {category.category.split(' ').slice(0, 2).join(' ')}
                              </Text>
                            </Flex>
                          )}
                        </Box>

                        {/* Right Side - Content */}
                        <Box flex="1" display="flex" flexDirection="column" justify="space-between">
                          {/* Header Section */}
                          <Box>
                            <Flex justify="space-between" align="start" mb={2}>
                              <Text 
                                fontSize="lg" 
                                fontWeight="bold" 
                                color={headerTextColor}
                                lineHeight="1.2"
                              >
                                {category.category}
                              </Text>
                              
                              {/* Action Buttons */}
                              <HStack spacing={1} mr={8}>
                                <IconButton
                                  icon={<EditIcon />}
                                  size="sm"
                                  colorScheme="blue"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(category);
                                  }}
                                  aria-label="Edit category"
                                />
                                <IconButton
                                  icon={<DeleteIcon />}
                                  size="sm"
                                  colorScheme="red"
                                  variant="ghost"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(category.id);
                                  }}
                                  aria-label="Delete category"
                                />
                              </HStack>
                            </Flex>

                            <HStack spacing={2} mb={3}>
                              <Badge 
                                colorScheme="blue" 
                                variant="subtle" 
                                fontSize="xs"
                                px={2}
                                py={1}
                                borderRadius="full"
                              >
                                {category.items?.length || 0} items
                              </Badge>
                              {category.category.toLowerCase().includes('favorite') && (
                                <Badge 
                                  colorScheme="yellow" 
                                  variant="solid" 
                                  fontSize="xs"
                                  px={2}
                                  py={1}
                                  borderRadius="full"
                                >
                                  ⭐ Featured
                                </Badge>
                              )}
                            </HStack>
                          </Box>

                          {/* Footer Section */}
                          <Flex justify="space-between" align="center">
                            <Button
                              size="sm"
                              colorScheme="blue"
                              variant="outline"
                              leftIcon={<AddIcon />}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(category);
                              }}
                              borderRadius="md"
                            >
                              Add Items
                            </Button>
                            
                            <Text fontSize="xs" color={idTextColor} fontWeight="medium">
                              ID: {category.id}
                            </Text>
                          </Flex>
                        </Box>
                      </Flex>

                      {/* Drag Handle */}
                      <Box
                        position="absolute"
                        top={3}
                        right={3}
                        opacity={0.3}
                        _hover={{ opacity: 1 }}
                        cursor="grab"
                        bg="white"
                        borderRadius="md"
                        p={1}
                        boxShadow="sm"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                      >
                        <Text fontSize="xs" color="gray.500" fontWeight="bold">⋮⋮</Text>
                      </Box>
                    </Box>
                  </Reorder.Item>
                );
              })}
            </VStack>
          </Reorder.Group>
        </Box>
      </>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        isCentered
        size={{ base: "full", md: "lg", lg: "4xl" }}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {currentCategory.id ? "Update Category" : "Create Category"}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl id="category" mb={4}>
              <FormLabel>Category Name</FormLabel>
              <Input
                placeholder="Enter category name"
                value={currentCategory.category}
                onChange={(e) =>
                  setCurrentCategory({
                    ...currentCategory,
                    category: e.target.value,
                  })
                }
                size="lg"
              />
            </FormControl>
            
            <FormControl id="picture" mb={4}>
              <FormLabel>Photo</FormLabel>
              <Box position="relative">
                <Image
                  src={
                    imageFile
                      ? URL.createObjectURL(imageFile)
                      : currentCategory.picture && typeof currentCategory.picture === 'string' && currentCategory.picture.trim() !== ""
                      ? getMediaURL(currentCategory.picture)
                      : "https://via.placeholder.com/320x240"
                  }
                  alt="Category Image"
                  width="100%"
                  height="300"
                  objectFit="cover"
                  borderRadius="lg"
                  fallbackSrc="https://via.placeholder.com/320x240"
                />
                <Button
                  onClick={() => imageInputRef.current.click()}
                  position="absolute"
                  leftIcon={currentCategory.id ? <GrUpdate /> : <AddIcon />}
                  colorScheme="blackAlpha"
                  variant="solid"
                  top={3}
                  right={3}
                  size="sm"
                  borderRadius="full"
                >
                  {currentCategory.id ? "Change image" : "Add image"}
                </Button>
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  ref={imageInputRef}
                  style={{ display: "none" }}
                />
              </Box>
              <Text fontSize="sm" color="gray.500" mt={2}>
                File requirement: JPG, PNG, GIF, or WEBP up to 10 MB. Minimum pixels required: 320 for width and height.
              </Text>
            </FormControl>

            <FormControl id="items" mb={4}>
              <FormLabel>Items</FormLabel>
              <ItemsListSection
                items={filteredItemsList}
                itemSearchQuery={itemSearchQuery}
                setItemSearchQuery={setItemSearchQuery}
                selectedItems={selectedItems}
                handleItemSelect={handleItemSelect}
                moveItem={moveItem}
                handleRemoveSelectedItem={handleRemoveSelectedItem}
              />
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button
              colorScheme="blue"
              mr={3}
              onClick={handleSubmit}
              size="lg"
              isLoading={submitting}
              loadingText={currentCategory.id ? "Updating..." : "Creating..."}
              disabled={submitting}
            >
              {currentCategory.id ? "Update Category" : "Create Category"}
            </Button>
            <Button onClick={handleClose} variant="ghost" size="lg">
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDeleteDialogOpen(false)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Category
            </AlertDialogHeader>
            <AlertDialogBody>
              Are you sure? You can't undo this action afterwards.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelRef}
                onClick={() => setIsDeleteDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button colorScheme="red" onClick={confirmDelete} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>

    {cropSrc && (
      <ImageCropModal
        open={!!cropSrc}
        imageSrc={cropSrc}
        onCropDone={handleCropDone}
        onCancel={handleCropCancel}
      />
    )}
  </>
  );
};

export default Categories;

